import { defineQueryOptions } from '@pinia/colada';
import { api, ApiError } from './client.ts';
import type { components } from './schema.gen.ts';
import { meetsStaleView, messageOf } from './serverFailure.ts';

export type Game = Omit<
  components['schemas']['LobbyView'],
  'mayStart' | 'phase' | 'phaseLengthsRange' | 'players' | 'roundsRange'
>;
export type GameConfig = components['schemas']['GameConfig'];
export type GameMode = components['schemas']['GameMode'];
export type GameView = components['schemas']['GameView'];
export type PauseReason = components['schemas']['PauseReason'];
export type PhaseLengthsChange = components['schemas']['ChangePhaseLengthsRequest'];
export type PhaseLengthsRange = components['schemas']['PhaseLengthsRange'];
export type Range = components['schemas']['Range'];

export type ErrorSlug = components['schemas']['ErrorResponse']['slug'];

export const noPlayerSession = 'no-player-session' satisfies ErrorSlug;

export const gameKeys = {
  root: ['game'] as const,
  view: ['game', 'view'] as const,
};

export const gameViewQuery = defineQueryOptions({
  key: gameKeys.view,
  gcTime: 0,
  query: async (): Promise<GameView | typeof noPlayerSession> => {
    const { data, error, response } = await api.GET('/game');
    if (error?.slug === noPlayerSession) return noPlayerSession;
    if (!data) throw new ApiError(response.status, error && messageOf('viewGame', error), error?.slug);
    return data;
  },
});

export const changeRounds = async (rounds: number): Promise<void> => {
  const { error, response } = await api.PUT('/game/rounds', { body: { rounds } });
  if (error && !meetsStaleView(error.slug))
    throw new ApiError(response.status, messageOf('changeRounds', error), error.slug);
};

export const changePhaseLengths = async (lengths: PhaseLengthsChange): Promise<void> => {
  const { error, response } = await api.PUT('/game/phase-lengths', { body: lengths });
  if (error && !meetsStaleView(error.slug))
    throw new ApiError(response.status, messageOf('changePhaseLengths', error), error.slug);
};

export const pauseGame = async (): Promise<void> => {
  const { error, response } = await api.POST('/game/pause');
  if (error && !meetsStaleView(error.slug)) {
    throw new ApiError(response.status, messageOf('pauseGame', error), error.slug);
  }
};

export const unpauseGame = async (): Promise<void> => {
  const { error, response } = await api.POST('/game/unpause');
  if (error && !meetsStaleView(error.slug)) {
    throw new ApiError(response.status, messageOf('unpauseGame', error), error.slug);
  }
};
